import React, {
  FC,
  ReactNode,
  ReactElement,
  useEffect,
  useRef,
  useState,
  createContext,
  useContext,
  memo,
  useMemo,
} from 'react';
import { useParams, useLocation, useIntl } from 'umi';
import { Card, Menu, Row, Col } from 'tea-component';
import {
  useCreation,
  useDebounceEffect,
  useMemoizedFn,
  useSetState,
  useGetState,
} from 'ahooks';
import InfoCard from '@/pages/intelligence/pages/TopCharts/Companies/components/GamesDetails/Financials/components/InfoCard';
// import { useAigcIntl } from '@/pages/aigc/utils/utils';
import { unitConversion } from '@/pages/dashboard/utils/utils';
import CardCont from './components/CardCont';
import {
  useAcquisitions,
  useExits,
  useFinancialsIPO,
  useFundingRounds,
  useInvestments,
  useInvestors,
} from './hooks';
import {
  acquisitionsTableData,
  fundingroundsTableData,
  investmentsTableData,
  investorsTableData,
} from './config/idnex';
// import { UseMonitorVisible } from './components/UseMonitorVisible';
import { checkListFn } from './components/TableSelect/utils';
import '@/pages/intelligence/assets/scss/TopCharts/Companies/GamesDetails/financials.scss';
import _ from 'lodash-es';
import companyDefaultIcon from '@/pages/intelligence/assets/imgs/common/company-default.svg';
import { getNodeProps } from '@intelligence/utils/domUtils';

type IModuleHref = 'ipostockprice' | 'investments' | 'investors' | 'fundingrounds' | 'acquisitions' | 'exits';
type IModulesLoadings = {
  [key in IModuleHref]: boolean;
};

interface MenuConfig<S = string> {
  id: IdKey;
  title: S;
  href: S;
}
const enum IdKey {
  'IPO&StockPrice' = 0,
  'Investments' = 1,
  'Investors' = 2,
  'FundingRounds' = 3,
  'Acquisitions' = 4,
  'Exits' = 5,
}
type IProps = {
  [key: string]: unknown;
  children?: ReactNode;
};
type ILeftProps = {
  [key: string]: unknown;
};
const menus: Array<MenuConfig> = [
  {
    id: IdKey['IPO&StockPrice'],
    title: 'dbi_company_ipo_stock_price',
    href: 'ipostockprice',
  },
  {
    id: IdKey.Investments,
    title: 'dbi_company_investments',
    href: 'investments',
  },
  { id: IdKey.Investors, title: 'dbi_company_investors', href: 'investors' },
  {
    id: IdKey.FundingRounds,
    title: 'dbi_company_founding_round',
    href: 'fundingrounds',
  },
  {
    id: IdKey.Acquisitions,
    title: 'dbi_company_acquisitions',
    href: 'acquisitions',
  },
  { id: IdKey.Exits, title: 'dbi_company_exits', href: 'exits' },
];
export const isError = (value: any): value is Error => value?.message;

export const ErrorBox = ({ error }: { error: unknown }) => {
  if (isError(error)) {
    return <div> {error?.message}</div>;
  }
  return null;
};

const FinancialContext = createContext<any>({});

const Financials: FC<IProps> = ({ containerRef, basicInfo }): ReactElement => {
  const { id } = useParams() as any;
  const { formatMessage } = useIntl();

  const listRef = useRef<HTMLDivElement>(null);
  // 吸顶滚动影响到左侧边栏fix，对应配合调整
  const [leftMenuFixed, setLeftMenuFixed] = useState(false);
  // 各个模块loading状态
  const [modulesLoadings, setModulesLoadings, getModulesLoadings] = useGetState<IModulesLoadings>({
    ipostockprice: true,
    investments: true,
    investors: true,
    fundingrounds: true,
    acquisitions: true,
    exits: true,
  });
  // 页面中所有模块是否有loading中的模块，某个模块还在loading中，页面就属于loading状态
  const isLoading = useMemo<boolean>(() => {
    return modulesLoadings && Object.values(modulesLoadings).some((item) => item);
  }, [modulesLoadings]);
  const updateModulesLoadings = (loadings: Partial<IModulesLoadings>) => {
    const prevLoadings = getModulesLoadings();
    const nextLoadings = { ...prevLoadings, ...loadings };
    setModulesLoadings(nextLoadings);
  };
  useEffect(() => {
    const handleScroll = (event: any) => {
      const { scrollTop } = event.srcElement;
      // 滚动判断数值开发调整
      if (scrollTop > 176) {
        setLeftMenuFixed(true);
      } else if (scrollTop < 170) {
        setLeftMenuFixed(false);
      }
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, []);

  return (
    <FinancialContext.Provider value={{ isLoading, id, formatMessage, containerRef, listRef, basicInfo, updateModulesLoadings }}>
      <div
        className={`dbi-financials${
          leftMenuFixed ? ' dbi-financials-leftfixed' : ''
        }`}
      >
        <LeftNav />
        <div ref={listRef} className="financials-rcont">
          <Ipostockprice />
          <Investments />
          <Investors />
          {/* TODO:这里初始化看情况还是调接口了，有时间处理一下，估计是初始化直接在当前屏幕展示了，导致report */}
          {/*<UseMonitorVisible>*/}
          {/*  <Fundingrounds />*/}
          {/*</UseMonitorVisible>*/}
          {/*<UseMonitorVisible>*/}
          {/*  <Acquisitions />*/}
          {/*</UseMonitorVisible>*/}
          {/*<UseMonitorVisible*/}
          {/*  onExpose={() => console.log('可见')}*/}
          {/*  onHide={() => console.log('消失')}*/}
          {/*>*/}
          {/*  <Exits />*/}
          {/*</UseMonitorVisible>*/}
          {/* Fundingrounds、Acquisitions、Exits 这三个模块移除懒加载机制，因为在没有加载出来这三个模块的情况下，点击左侧菜单 自动定位到这三个模块时，会因为没加载 导致滚动距离计算错误 */}
          <Fundingrounds />
          <Acquisitions />
          <Exits />
        </div>
      </div>
    </FinancialContext.Provider>
  );
};
export default Financials;

export const LeftNav: FC<ILeftProps> = () => {
  const location = useLocation();
  const { isLoading, formatMessage, containerRef, listRef } = useContext(FinancialContext);
  const searchParams = new URLSearchParams(location?.search);
  const initialActive = searchParams.get('anchor');
  const [active, setActive] = useState(initialActive || 'ipostockprice');
  const isActiveScroll = useRef(false);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, []);

  const handleScroll = (event: any) => {
    const { scrollTop } = event.srcElement;
    menus.map((task) => {
      const containerRef = document.getElementById(task?.href || '')?.offsetTop;
      if (!isActiveScroll.current && scrollTop >= containerRef!) {
        setActive(task.href);
      }
    });
  };
  const getDisanceByHref = (href?: string | null): number => {
    if (!href) return 0;
    const hrefElement = document.getElementById(href);
    if (!hrefElement) return 0;
    let distance = 0;
    const children = listRef?.current?.children || [];
    for (const child of children) {
      if (child === hrefElement) break;
      const {
        height,
        'margin-top': marginTop,
        'margin-bottom': marginBottom,
      } = getNodeProps(child, [
        { name: 'height', type: 'number' },
        { name: 'margin-top', type: 'number' },
        { name: 'margin-bottom', type: 'number' },
      ]);
      distance = distance + ((height as number) + (marginTop as number) + (marginBottom as number));
    }
    return distance;
  };

  const handleOnClickScrollId = (
    e: React.MouseEvent<Element, MouseEvent> | null,
    { href }: { href: string },
  ) => {
    if (!containerRef?.current) return false;
    if (!listRef?.current) return false;
    if (!href) return false;
    e?.preventDefault();
    setActive(href);
    isActiveScroll.current = true;
    const distance = getDisanceByHref(href);
    containerRef?.current?.scrollTo({
      // 这里distance不为0的情况下，在计算完需要滚动的模块总距离后，还需要加上112px的偏移量，才能保证当前模块顶部能跟左侧菜单对齐
      top: distance !== 0 ? distance + 112 : distance,
      behavior: 'smooth',
    });
    setTimeout(() => {
      isActiveScroll.current = false;
    }, 1000);
  };

  useEffect(() => {
    if (isLoading) return;
    const distance = getDisanceByHref(initialActive);
    isActiveScroll.current = true;
    containerRef?.current?.scrollTo({
      // 这里distance不为0的情况下，在计算完需要滚动的模块总距离后，还需要加上112px的偏移量，才能保证当前模块顶部能跟左侧菜单对齐
      top: distance !== 0 ? distance + 112 : distance,
      behavior: 'smooth',
    });
    setTimeout(() => {
      isActiveScroll.current = false;
    }, 1000);
  }, [isLoading]);

  return (
    <Card className="financials-lmenu">
      <Card.Body>
        <Menu>
          {menus.map(({ id, href, title }) => (
            <Menu.Item
              key={id}
              selected={href == active}
              title={formatMessage({ id: title })}
              onClick={(event) => handleOnClickScrollId(event, { href })}
            />
          ))}
        </Menu>
      </Card.Body>
    </Card>
  );
};
export const Ipostockprice = memo((): ReactElement => {
  const { id, formatMessage, basicInfo, updateModulesLoadings } = useContext(FinancialContext);
  const { data: financialsIpoData, loading } = useFinancialsIPO(
    useCreation(() => ({ company_id: id }), [id]),
    {
      onFinally: () => {
        updateModulesLoadings({ ipostockprice: false });
      },
    },
  );

  const dataList = useCreation(
    () => [
      {
        id: '0',
        text: 'dbi_company_symbol',
        data: `${financialsIpoData?.stock_exchange_symbol || '-'}:${
          financialsIpoData?.stock_symbol || '-'
        }`,
      },
      {
        id: '1',
        text: 'dbi_company_exiValuatlin',
        data: `$ ${financialsIpoData?.valuation || '-'}`,
      },
      {
        id: '2',
        text: 'dbi_company_ipo_date',
        data: financialsIpoData?.went_public_on || '-',
      },
    ],
    [financialsIpoData],
  );
  const Title = () => (
    <div className="financials-hd">
      <div className="financials-hd-title">
        {' '}
        {formatMessage({ id: 'dbi_company_ipo_stock_price' })}
      </div>
      <div className="financials-hd-text">
        <span>
          {basicInfo?.name}
          {formatMessage({ id: 'dbi_company_ticker' })}
        </span>
        <span className="financials-hd-hkg">
          {`${financialsIpoData?.stock_exchange_symbol || '-'}:${
            financialsIpoData?.stock_symbol || '-'
          }`}
        </span>
      </div>
    </div>
  );
  return <CardCont id="ipostockprice" title={<Title />} dataList={dataList} />;
});
export const Investments = memo((): ReactElement => {
  const { id, formatMessage, updateModulesLoadings } = useContext(FinancialContext);

  const [paramsDefault, setParamsDefault] = useSetState<{
    [key in string]: any;
  }>({
    page: 1,
    page_size: 5,
    // 升序||降序
    announced_date_sort: '',
    amount_raised_sort: '',
    name: [],
    // 0 yes ，1 no ，2 all
    lead_investor: '2',
    funding_round: [],
    company_id: id,
    order_metric: 'announced_on',
    order: 'asc',
  });
  const {
    data: investList,
    error,
    loading,
  } = useInvestments(
    useCreation(
      () => ({
        ...paramsDefault,
      }),
      [paramsDefault],
    ),
    {
      onFinally: () => {
        updateModulesLoadings({ investments: false });
      },
    },
  );

  const [defauleList, setDefauleList] = useState([]);

  useDebounceEffect(
    () => {
      setDefauleList((pre: any) =>
        _.uniqBy(
          [...(pre || []), ...(investList?.record_list || [])],
          'announce_date',
        ),
      );
    },
    [investList?.record_list],
    {
      wait: 400,
    },
  );
  const DropdownList = useCreation(
    () => checkListFn(defauleList, 'investments'),
    [defauleList, investList?.record_list],
  );
  const dataList = [
    {
      id: '0',
      text: 'dbi_company_number_investments',
      data: investList?.investments_num,
    },
    {
      id: '1',
      text: 'dbi_company_number_lead_investments',
      data: investList?.investments_lead_num,
    },
  ];

  const handleOnChangePageIng = useMemoizedFn(
    ({
      pageIndex: page,
      pageSize: page_size,
    }: {
      pageIndex: number;
      pageSize: number;
    }) => {
      setParamsDefault((pre) => ({
        ...pre,
        page,
        page_size,
      }));
    },
  );

  const handleOnClickTableSort = (dataSource?: any) => {
    const [order] = _.compact(
      Object.keys(dataSource).map((task) => {
        if (dataSource[task]) {
          return [dataSource[task], task];
        }
      }),
    );
    setParamsDefault((pre: any) => ({
      ...pre,
      ...dataSource,
      order: order?.[0] || 'asc',
      order_metric: order?.[1] || 'announced_on',
      page: 1,
    }));
  };

  return (
    <CardCont
      {...{
        loading,
        dataList,
        title: formatMessage({ id: 'dbi_company_investments' }),
        tableData: investmentsTableData(
          investList?.record_list,
          paramsDefault,
          DropdownList,
          setParamsDefault,
          formatMessage,
        ),
        id: 'investments',
        total: investList?.investments_num,
        pageIndex: paramsDefault.page,
        pageSize: paramsDefault.page_size,
        onPagingChange: handleOnChangePageIng,
        OnClickTableSort: handleOnClickTableSort,
      }}
    />
  );
});
export const Investors = memo((): ReactElement => {
  const { id, formatMessage, updateModulesLoadings } = useContext(FinancialContext);
  const [paramsDefault, setParamsDefault] = useSetState<{
    [key in string]: any;
  }>({
    company_id: id,
    investor_name: [],
    lead_investor: '2',
    funding_round: [],
    partners: [],
    page: 1,
    page_size: 5,
  });
  const { data, error, loading } = useInvestors(
    useCreation(
      () => ({
        ...paramsDefault,
      }),
      [paramsDefault],
    ),
    {
      onFinally: () => {
        updateModulesLoadings({ investors: false });
      },
    },
  );

  const [defauleList, setDefauleList] = useState([]);

  useDebounceEffect(
    () => {
      setDefauleList((pre: any) =>
        _.uniqBy([...(pre || []), ...(data?.investors || [])], 'investor'),
      );
    },
    [data?.investors],
    {
      wait: 400,
    },
  );
  const DropdownList = useCreation(
    () => checkListFn(defauleList, 'investors'),
    [data?.investors, defauleList],
  );

  const dataList = [
    {
      id: '0',
      text: 'dbi_company_number_investors',
      data: data?.total_num_inverstors,
    },
    {
      id: '1',
      text: 'dbi_company_number_lead_investors',
      data: data?.total_num_lead_inverstors,
    },
  ];
  const handleOnChangePageIng = useMemoizedFn(
    ({
      pageIndex: page,
      pageSize: page_size,
    }: {
      pageIndex: number;
      pageSize: number;
    }) => {
      setParamsDefault((pre) => ({
        ...pre,
        page,
        page_size,
      }));
    },
  );
  return (
    <CardCont
      id="investors"
      title={formatMessage({ id: 'dbi_company_investors' })}
      dataList={dataList}
      loading={loading}
      total={data?.total_num_inverstors}
      pageIndex={paramsDefault.page}
      pageSize={paramsDefault.page_size}
      onPagingChange={handleOnChangePageIng}
      tableData={investorsTableData(
        data?.investors,
        paramsDefault,
        DropdownList,
        setParamsDefault,
        formatMessage,
      )}
    />
  );
});
export const Fundingrounds = memo((): ReactElement => {
  const { id, formatMessage, updateModulesLoadings } = useContext(FinancialContext);
  const [paramsDefault, setParamsDefault] = useSetState<{
    [key in string]: any;
  }>({
    company_id: id,
    amount_raised_sort: '',
    number_of_investors_sort: '',
    announced_date_sort: '',
    lead_investors: [],
    transaction_name: [],
    page: 1,
    page_size: 5,
    order_metric: 'announced_on',
    order: 'asc',
  });

  const { data, error, loading } = useFundingRounds(
    useCreation(
      () => ({
        ...paramsDefault,
      }),
      [paramsDefault],
    ),
    {
      onFinally: () => {
        updateModulesLoadings({ fundingrounds: false });
      },
    },
  );
  const [defauleList, setDefauleList] = useState([]);
  useDebounceEffect(
    () => {
      setDefauleList((pre: any) =>
        _.uniqBy(
          [...(pre || []), ...(data?.funding_rounds_list || [])],
          'announced_on',
        ),
      );
    },
    [data?.funding_rounds_list],
    {
      wait: 400,
    },
  );

  const DropdownList = useCreation(
    () => checkListFn(defauleList, 'fundingRounds'),
    [defauleList, data?.funding_rounds_list],
  );
  // console.log(DropdownList)
  const handleOnChangePageIng = useMemoizedFn(
    ({
      pageIndex: page,
      pageSize: page_size,
    }: {
      pageIndex: number;
      pageSize: number;
    }) => {
      setParamsDefault((pre) => ({
        ...pre,
        page,
        page_size,
      }));
    },
  );
  const handleOnClickTableSort = (dataSource?: any) => {
    const [order] = _.compact(
      Object.keys(dataSource).map((task) => {
        if (dataSource[task]) {
          return [dataSource[task], task];
        }
      }),
    );
    setParamsDefault((pre: any) => ({
      ...pre,
      ...dataSource,
      order: order?.[0] || 'asc',
      order_metric: order?.[1] || 'announced_on',
      page: 1,
    }));
  };

  const dataList = [
    {
      id: '0',
      text: 'dbi_company_number_funding_rounds',
      data: data?.total_funding_rounds,
    },
    {
      id: '1',
      text: 'dbi_company_total_funding_amount',
      data: `$${unitConversion?.(data?.total_amount as string)}`,
    },
  ];
  return (
    <CardCont
      loading={loading}
      id="fundingrounds"
      title={formatMessage({ id: 'dbi_company_founding_round' })}
      dataList={dataList}
      total={data?.total_funding_rounds}
      pageIndex={paramsDefault.page}
      pageSize={paramsDefault.page_size}
      onPagingChange={handleOnChangePageIng}
      OnClickTableSort={handleOnClickTableSort}
      tableData={fundingroundsTableData(
        data?.funding_rounds_list,
        paramsDefault,
        DropdownList,
        setParamsDefault,
        formatMessage,
      )}
    />
  );
});
export const Acquisitions = (): ReactElement => {
  const { id, formatMessage, updateModulesLoadings } = useContext(FinancialContext);
  const [paramsDefault, setParamsDefault] = useSetState<{
    [key in string]: any;
  }>({
    company_id: id,
    acquiree_name: [],
    announced_date_sort: '',
    price_sort: '',
    transaction_name: [],
    page: 1,
    page_size: 5,
    order_metric: 'price',
    order: 'asc',
  });
  const { data, error, loading } = useAcquisitions(
    useCreation(
      () => ({
        ...paramsDefault,
      }),
      [paramsDefault],
    ),
    {
      onFinally: () => {
        updateModulesLoadings({ acquisitions: false });
      },
    },
  );
  const [defauleList, setDefauleList] = useState([]);

  useDebounceEffect(
    () => {
      setDefauleList((pre: any) =>
        _.uniqBy(
          [...(pre || []), ...(data?.acquisitions || [])],
          'announces_date',
        ),
      );
    },
    [data?.acquisitions],
    {
      wait: 400,
    },
  );

  const DropdownList = useCreation(
    () => checkListFn(defauleList, 'acquisitions'),
    [data?.acquisitions, defauleList],
  );

  const handleOnChangePageIng = useMemoizedFn(
    ({
      pageIndex: page,
      pageSize: page_size,
    }: {
      pageIndex: number;
      pageSize: number;
    }) => {
      setParamsDefault((pre) => ({
        ...pre,
        page,
        page_size,
      }));
    },
  );

  const handleOnClickTableSort = useMemoizedFn((dataSource?: any) => {
    const [order] = _.compact(
      Object.keys(dataSource).map((task) => {
        if (dataSource[task]) {
          return [dataSource[task], task];
        }
      }),
    );
    setParamsDefault((pre: any) => ({
      ...pre,
      ...dataSource,
      order: order?.[0] || 'asc',
      order_metric: order?.[1] || 'price',
      price_sort: order?.[1] === 'price' ? order?.[0] : '',
      page: 1,
    }));
  });
  const dataList = [
    {
      id: '0',
      text: 'dbi_company_number_acquisitions',
      data: data?.total_num_acquisitions,
    },
  ];
  return (
    <CardCont
      loading={loading}
      id="acquisitions"
      title={formatMessage({ id: 'dbi_company_acquisitions' })}
      dataList={dataList}
      total={data?.total_num_acquisitions}
      pageIndex={paramsDefault.page}
      pageSize={paramsDefault.page_size}
      onPagingChange={handleOnChangePageIng}
      OnClickTableSort={handleOnClickTableSort}
      tableData={acquisitionsTableData(
        data?.acquisitions,
        paramsDefault,
        DropdownList,
        setParamsDefault,
        formatMessage,
      )}
    />
  );
};
export const Exits = memo((): ReactElement => {
  const { id, formatMessage, updateModulesLoadings } = useContext(FinancialContext);
  const { data, error, loading } = useExits(
    useCreation(() => ({ company_id: id }), [id]),
    {
      onFinally: () => {
        updateModulesLoadings({ exits: false });
      },
    },
  );
  const dataList = [
    {
      id: '0',
      text: 'dbi_company_number_exits',
      data: data?.total,
    },
  ];
  return (
    <>
      <ErrorBox {...{ error }} />
      <CardCont
        id="exits"
        title={formatMessage({ id: 'dbi_company_exits' })}
        dataList={dataList}
      >
        <Row className="financials-list">
          {data?.record_list?.map(
            (e: { image_id: React.Key | null | undefined }) => (
              <Col key={e.image_id} span={12}>
                <InfoCard infoList={e} defaultImg={companyDefaultIcon} />
              </Col>
            ),
          )}
        </Row>
      </CardCont>
    </>
  );
});
